import { Button } from '@xhubio-saas/core-frontend/ui-components'
import { FileUp } from 'lucide-react'
import { type DragEvent, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface DroppedDocument {
  name: string
  size: number
  /** PDF als Base64 (ohne `data:`-Vorspann), XML als Text — so, wie `convertToJson` des Pakets es erwartet. */
  content: string
  contentType: 'application/pdf' | 'text/xml'
}

/** Base64 aus einer Datei — ohne den `data:`-Vorspann. */
async function toBase64(file: File): Promise<string> {
  const buffer = new Uint8Array(await file.arrayBuffer())
  let raw = ''
  for (let i = 0; i < buffer.length; i += 0x8000) {
    raw += String.fromCharCode(...buffer.subarray(i, i + 0x8000))
  }
  return btoa(raw)
}

/**
 * Ablage fuer eine E-Rechnung als PDF oder XML (ZUGFeRD/Factur-X lesen) — dieselbe Bauform wie die Ablage der
 * Validierung: ohne Datei die ganze Flaeche ein `<button>`, mit Datei eine schmale Zeile mit eigenem Knopf (Knopf in
 * Knopf waere ungueltiges HTML). Ersetzt die nicht exportierte `PdfDropzone` des Pakets; gelesen wird wie dort: PDF
 * als Base64, XML als Text, eine Datei ohne Endung nur, wenn sie wie XML beginnt.
 */
export function DocumentDrop({
  value,
  onChange,
}: {
  value: DroppedDocument | null
  onChange: (doc: DroppedDocument | null) => void
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [rejected, setRejected] = useState(false)

  const read = useCallback(
    async (file: File) => {
      const name = file.name.toLowerCase()
      const isPdf = file.type === 'application/pdf' || name.endsWith('.pdf')
      const isXml =
        file.type === 'text/xml' || file.type === 'application/xml' || name.endsWith('.xml')
      if (isPdf) {
        onChange({
          name: file.name,
          size: file.size,
          content: await toBase64(file),
          contentType: 'application/pdf',
        })
      } else {
        const text = await file.text()
        if (!isXml && !text.trim().startsWith('<')) {
          setRejected(true)
          return
        }
        onChange({ name: file.name, size: file.size, content: text, contentType: 'text/xml' })
      }
      setRejected(false)
    },
    [onChange]
  )

  const handlers = useMemo(
    () => ({
      onDragOver: (e: DragEvent<HTMLElement>) => {
        e.preventDefault()
        setDragOver(true)
      },
      onDragLeave: () => setDragOver(false),
      onDrop: (e: DragEvent<HTMLElement>) => {
        e.preventDefault()
        setDragOver(false)
        const file = e.dataTransfer.files?.[0]
        if (file) void read(file)
      },
    }),
    [read]
  )

  return (
    <div className="space-y-2">
      {value ? (
        <div
          data-testid="invoice-conversion-document"
          className={`flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-4 py-2 ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border'
          }`}
          {...handlers}
        >
          <FileUp className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-32 flex-1 truncate text-sm tabular-nums">
            {t('invoiceConversionPage.input.fileLine', {
              name: value.name,
              kb: Math.max(1, Math.round(value.size / 1024)),
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto shrink-0 whitespace-nowrap"
            onClick={() => inputRef.current?.click()}
          >
            {t('invoiceConversionPage.input.otherFile')}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="invoice-conversion-document"
          className={`flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-[background-color,border-color] motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring ${
            dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 bg-card'
          }`}
          onClick={() => inputRef.current?.click()}
          {...handlers}
        >
          <FileUp className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
          <span className="font-medium">{t('invoiceConversionPage.input.dropTitle')}</span>
          <span className="text-sm text-muted-foreground">
            {t('invoiceConversionPage.input.dropHint')}
          </span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".xml,.pdf,application/xml,text/xml,application/pdf"
        className="hidden"
        data-testid="invoice-conversion-document-file"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void read(file)
        }}
      />
      {rejected && (
        <p className="text-sm text-destructive" role="alert">
          {t('invoiceConversionPage.input.rejected')}
        </p>
      )}
    </div>
  )
}
